import {mapChatMessagesToApiHistory} from '../../aiMessageGenerator/utils/types';
import {getAiAgentContext, sendAiAgentMessage} from '../../../redux/aiAgent/aiAgentActions';
import {resolveChatLang} from './detectChatLang';
import {resolveAttachmentImage} from './resolveAttachmentImage';
import {buildAgentTurnResult} from './resolveAgentTurn';
import {rememberTurnContext} from './rememberTurnContext';
import {rememberEventTypeFromAsk} from './agentSessionMemory';
import {clearHeldProposal, holdProposal, isProposalConfirmPrompt} from './agentProposalHold';
import {confirmHeldProposal, runConfirmedProposal} from './confirmProposal';
import type {AiAgentConfirmOptions} from './confirmProposal';
import type {NavigateFn} from './navigateAfterExecute';
import type {AiAgentSubmitHandler} from '../types';

type AiAgentSubmitOptions = AiAgentConfirmOptions & {
    imageTooLarge: string;
    imageUnsupported: string;
};

export function createAiAgentSubmitHandler(
    navigate: NavigateFn,
    options: AiAgentSubmitOptions,
): AiAgentSubmitHandler {
    return async (prompt, history, sessionId, context) => {
        const trimmedPrompt = prompt.trim();
        if(isProposalConfirmPrompt(trimmedPrompt)) {
            return confirmHeldProposal(sessionId, navigate, options);
        }
        // Anything else answers the summary with a change, so the held proposal is stale —
        // the server re-proposes with the change, and that turn holds the new one.
        clearHeldProposal();
        rememberEventTypeFromAsk(trimmedPrompt, context.pendingAsk);

        const {
            eid, pid, scope, currentRoute, formId, eventType, sessionIntent, currentFormValues, recordCreated,
        } = getAiAgentContext();
        if(!eid && !pid) {
            return null;
        }

        const {dataUrl: imageDataUrl, error: imageError} = await resolveAttachmentImage(context.attachment);
        if(imageError) {
            return {text: imageError === 'tooLarge' ? options.imageTooLarge : options.imageUnsupported};
        }
        if(!trimmedPrompt && !imageDataUrl) {
            return null;
        }

        const agentPayload = await sendAiAgentMessage({
            prompt: trimmedPrompt,
            history: mapChatMessagesToApiHistory(history),
            sessionId,
            scope,
            currentRoute,
            eid,
            pid,
            formId,
            eventType,
            sessionIntent,
            chatLang: resolveChatLang(trimmedPrompt, context.pendingAsk),
            currentFormValues,
            recordCreated,
            imageDataUrl,
            pendingAsk: context.pendingAsk
                ? {question: context.pendingAsk.question, options: context.pendingAsk.options}
                : null,
        });

        if(!agentPayload) {
            return null;
        }

        rememberEventTypeFromAsk(agentPayload.answeredOption?.value, context.pendingAsk);
        rememberTurnContext(agentPayload);

        const {result, readyToConfirm} = buildAgentTurnResult(agentPayload);
        // They agreed to the summary in words instead of pressing. The agreement is the
        // confirmation, so the record is created here — otherwise the chat answers "yes" with
        // the same summary again, and the flow has no way to end.
        if(agentPayload.pendingProposal?.confirmed) {
            return runConfirmedProposal(agentPayload.pendingProposal, {scope, eid, pid}, sessionId, navigate, options);
        }
        if(readyToConfirm && agentPayload.pendingProposal) {
            // The summary asks for confirmation — this turn only holds the proposal, and the
            // chat offers the action. Creating here would answer the question on the
            // producer's behalf. The owner is stamped now: it is the one that was agreed on,
            // and the confirmation may come after the producer has navigated elsewhere.
            holdProposal(agentPayload.pendingProposal, {scope, eid, pid});
        }
        return result;
    };
}
